/** @format */

import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { Icon, _$ } from '@king-design/react'

export interface ExpandContainerProps {
  className?: string
  /** 标题区域，点击可展开/收起（disabled 时除外） */
  title?: React.ReactNode
  children?: React.ReactNode
  /** 非受控：默认是否展开 */
  defaultExpanded?: boolean
  /** 受控：是否展开 */
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  /** 收起 / 展开文案，不传则使用默认国际化文案 */
  expandText?: string
  collapseText?: string
  /** 标题行右侧自定义内容（在切换按钮左侧） */
  headerExtra?: React.ReactNode
  /** 禁用时不响应展开收起 */
  disabled?: boolean
  /** 展开/收起动画时长（ms），默认 300 */
  duration?: number
}

const ExpandContainer: React.FC<ExpandContainerProps> = ({
  className,
  title,
  children,
  defaultExpanded = true,
  expanded: expandedProp,
  onExpandedChange,
  expandText = _$('展开'),
  collapseText = _$('收起'),
  headerExtra,
  disabled = false,
  duration = 300
}) => {
  const [innerExpanded, setInnerExpanded] = useState(defaultExpanded)
  const isControlled = expandedProp !== undefined
  const expanded = isControlled ? expandedProp : innerExpanded

  const setExpanded = useCallback(
    (next: boolean) => {
      if (disabled) return
      if (!isControlled) {
        setInnerExpanded(next)
      }
      onExpandedChange?.(next)
    },
    [disabled, isControlled, onExpandedChange]
  )

  const toggle = useCallback(() => {
    setExpanded(!expanded)
  }, [expanded, setExpanded])

  const durationSec = `${duration}ms`

  return (
    <div className={className} style={{ '--expand-duration': durationSec } as React.CSSProperties}>
      <div className={`expand-container__header${disabled ? ' is-disabled' : ''}`}>
        {title != null ? <div className="expand-container__title">{title}</div> : null}
        <div className="expand-container__actions">
          <button
            type="button"
            className="expand-container__toggle"
            disabled={disabled}
            onClick={e => {
              e.stopPropagation()
              toggle()
            }}
          >
            <span className="expand-container__toggle-text">
              {expanded ? collapseText : expandText}
            </span>
            <Icon
              className={`expand-container__toggle-icon ${
                expanded ? 'k-icon-up-circled' : 'k-icon-down-circled'
              }`}
              size={16}
            />
          </button>
        </div>
        {headerExtra ? <div className="expand-container__header-extra">{headerExtra}</div> : null}
      </div>
      <div
        className={`expand-container__body-wrap${expanded ? ' is-expanded' : ''}`}
        aria-hidden={!expanded}
      >
        <div className="expand-container__body-inner">
          <div className="expand-container__body">{children}</div>
        </div>
      </div>
    </div>
  )
}

export default styled(ExpandContainer)`
  .expand-container__header {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 32px;

    &.is-disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .expand-container__title {
    min-width: 0;
    font-size: 14px;
    font-weight: 500;
    color: #262626;
    margin-right: 4px;
    line-height: 22px;
  }

  .expand-container__actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    text-align: center;
  }

  .expand-container__toggle {
    display: inline-flex;
    align-items: center;
    padding: 0 4px;
    border: none;
    background: transparent;
    color: #0091ea;
    font-size: 12px;
    line-height: 20px;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }

    .expand-container__toggle-icon {
      color: inherit;
      transition: transform var(--expand-duration, 300ms) ease-in-out;
    }
  }

  .expand-container__header-extra {
    color: #999;
  }
  .expand-container__body-wrap {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows var(--expand-duration, 300ms) ease-in-out;

    &.is-expanded {
      grid-template-rows: 1fr;
    }
  }

  .expand-container__body-inner {
    overflow: hidden;
    min-height: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity var(--expand-duration, 300ms) ease-in-out;
  }

  .expand-container__body-wrap.is-expanded .expand-container__body-inner {
    opacity: 1;
    pointer-events: auto;
  }
`
